import { useState, useEffect } from "react";
import { useForm } from "@tanstack/react-form";
import { machineLogSchema, MachineLogFormValues, toMachineLogPayload, machineLogToFormValues } from "@/lib/schemas/factory";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ImageDropzone } from "@/components/shared/ImageDropzone";
import { SharedCombobox } from '@/components/shared/SharedCombobox';
import { ComboboxConfigMaster } from '@/components/shared/ComboboxConfigMaster';

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Activity, AlertTriangle, Wrench } from "lucide-react";
import { Label } from "@/components/ui/label";
import { NumericInput } from "@/components/ui/numeric-input";

import { toVnDateString } from "@apv-erp/status";
export type MachineLogPayload = ReturnType<typeof toMachineLogPayload>;

interface MachineLogFormProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** payload camelCase cho machineLogs.create/update; id = id bản ghi khi sửa */
    onSubmit: (payload: MachineLogPayload, id?: string) => void | Promise<void>;
    /** Bản ghi nhật ký máy từ API (machineLogs.getById/list) khi sửa */
    initialData?: any;
}

const EMPTY_VALUES: MachineLogFormValues = {
    logId: "",
    machineId: "",
    productionOrderId: "",
    date: toVnDateString(),
    shift: "CA_1",
    type: "OPERATION",
    durationMinutes: 60,
    operatorId: "",
    qtyOutput: 0,
    qtyWaste: 0,
    parameters: { speed: "", temperature: "", pressure: "" },
    issueType: "",
    reason: "",
    fixAction: "",
    attachments: [],
};

export function MachineLogForm({ open, onOpenChange, onSubmit, initialData }: MachineLogFormProps) {
    const isEditMode = !!initialData?.id;

    const form = useForm({
        defaultValues: { ...EMPTY_VALUES, ...machineLogToFormValues(initialData) } as MachineLogFormValues,
        onSubmit: async ({ value }) => {
            const result = machineLogSchema.safeParse(value);
            if (!result.success) {
                result.error.errors.forEach(err => {
                    const path = err.path.join(".");
                    form.setFieldMeta(path as any, (prev) => ({
                        ...prev,
                        errors: [err.message]
                    }));
                });
                return;
            }
            await onSubmit(toMachineLogPayload(result.data), initialData?.id);
        }
    });

    useEffect(() => {
        if (open) {
            form.reset({ ...EMPTY_VALUES, date: toVnDateString(), ...machineLogToFormValues(initialData) } as MachineLogFormValues);
        }
    }, [open, initialData, form]);

    const FGen = ({ name, label, children, description, inline }: any) => (
        <form.Field name={name}>
            {(field) => (
                <div className={inline ? "flex items-center gap-4" : "space-y-1.5"} data-testid={`machine-log-field-${name}`}>
                    {label && <Label className={field.state.meta.errors.length ? "text-red-500" : ""}>{label}</Label>}
                    {children(field)}
                    {description && <p className="text-sm text-slate-500">{description}</p>}
                    {field.state.meta.errors.length > 0 && (
                        <p className="text-sm text-red-500">{field.state.meta.errors[0]}</p>
                    )}
                </div>
            )}
        </form.Field>
    );

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="text-xl font-bold text-slate-900 flex items-center gap-2">
                        {isEditMode ? "Chỉnh sửa Nhật ký" : "Ghi Nhật ký Máy"}
                        <span className="text-sm font-normal text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                            {isEditMode ? initialData.logId : "Mới"}
                        </span>
                    </DialogTitle>
                    <DialogDescription>
                        Ghi lại hoạt động vận hành, sản lượng hoặc sự cố dừng máy.
                    </DialogDescription>
                </DialogHeader>

                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        form.handleSubmit();
                    }}
                    className="space-y-6 py-4"
                >
                    {/* Common Fields */}
                    <div className="grid grid-cols-2 gap-4">
                        <FGen name="machineId" label="Chọn Máy">
                            {(field: any) => (
                                // Lưu id máy (FK machine_logs.machine_id → machines.id); ô chọn chung có tìm kiếm + thêm máy mới theo quyền (03/11/2026)
                                <SharedCombobox
                                    type="machine"
                                    value={field.state.value}
                                    onChange={(val) => field.handleChange(val)}
                                    placeholder="Chọn máy..."
                                />
                            )}
                        </FGen>
                        <FGen name="productionOrderId" label="Lệnh Sản Xuất (LSX)">
                            {(field: any) => (
                                <SharedCombobox
                                    type="productionOrder"
                                    value={field.state.value}
                                    onChange={(val) => field.handleChange(val)}
                                    placeholder="Chọn LSX..."
                                    params={{ forFactory: true }}
                                />
                            )}
                        </FGen>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                        <FGen name="date" label="Ngày">
                            {(field: any) => (
                                <Input type="date" value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} />
                            )}
                        </FGen>
                        <FGen name="shift" label="Ca làm việc">
                            {(field: any) => (
                                <ComboboxConfigMaster
                                    categoryCode="shifts"
                                    value={field.state.value}
                                    onChange={field.handleChange}
                                    placeholder="Chọn ca..."
                                    hideLabel
                                />
                            )}
                        </FGen>
                        <FGen name="operatorId" label="Người vận hành">
                            {(field: any) => (
                                <SharedCombobox
                                    type="user"
                                    params={{ scope: 'production' }} // nhân sự theo quy trình (03/11/2026)
                                    value={field.state.value || ""}
                                    onChange={(val) => field.handleChange(val)}
                                    placeholder="Chọn nhân viên..."
                                    hideLabel
                                />
                            )}
                        </FGen>
                    </div>

                    <Separator />

                    {/* Log Type Selection */}
                    <FGen name="type" label="Loại Nhật ký">
                        {(field: any) => (
                            <div className="grid grid-cols-3 gap-4 mt-2">
                                <div
                                    className={`cursor-pointer border rounded-lg p-4 flex flex-col items-center justify-center gap-2 hover:bg-slate-50 transition-all ${field.state.value === 'OPERATION' ? 'border-emerald-500 bg-emerald-50 text-emerald-700 ring-1 ring-emerald-500' : 'border-slate-200'}`}
                                    onClick={() => field.handleChange('OPERATION')}
                                >
                                    <Activity className="w-6 h-6" />
                                    <span className="font-semibold text-sm">Vận hành</span>
                                </div>
                                <div
                                    className={`cursor-pointer border rounded-lg p-4 flex flex-col items-center justify-center gap-2 hover:bg-slate-50 transition-all ${field.state.value === 'DOWNTIME' ? 'border-rose-500 bg-rose-50 text-rose-700 ring-1 ring-rose-500' : 'border-slate-200'}`}
                                    onClick={() => field.handleChange('DOWNTIME')}
                                >
                                    <AlertTriangle className="w-6 h-6" />
                                    <span className="font-semibold text-sm">Dừng máy</span>
                                </div>
                                <div
                                    className={`cursor-pointer border rounded-lg p-4 flex flex-col items-center justify-center gap-2 hover:bg-slate-50 transition-all ${field.state.value === 'MAINTENANCE' ? 'border-blue-500 bg-blue-50 text-blue-700 ring-1 ring-blue-500' : 'border-slate-200'}`}
                                    onClick={() => field.handleChange('MAINTENANCE')}
                                >
                                    <Wrench className="w-6 h-6" />
                                    <span className="font-semibold text-sm">Bảo trì</span>
                                </div>
                            </div>
                        )}
                    </FGen>

                    <form.Subscribe selector={(state) => [state.values.type, state.values.qtyWaste]}>
                        {([watchType, watchQtyWaste]) => (
                            <>
                                {/* OPERATION SPECIFIC FIELDS */}
                                {watchType === 'OPERATION' && (
                                    <div className="bg-emerald-50/50 p-4 rounded-lg border border-emerald-100 space-y-4 animate-in fade-in slide-in-from-top-2">
                                        <h3 className="text-sm font-bold text-emerald-800 uppercase tracking-widest flex items-center gap-2">
                                            <Activity className="w-4 h-4" /> Chi tiết Vận hành
                                        </h3>

                                        <div className="grid grid-cols-3 gap-4">
                                            <FGen name="durationMinutes" label="Thời gian chạy (phút)">
                                                {(field: any) => (
                                                    <NumericInput value={field.state.value} onChange={(e) => field.handleChange(Number(e.target.value))} />
                                                )}
                                            </FGen>
                                            <FGen name="qtyOutput" label="Sản lượng (Tờ/Cái)">
                                                {(field: any) => (
                                                    <NumericInput className="font-bold text-slate-900" value={field.state.value} onChange={(e) => field.handleChange(Number(e.target.value))} />
                                                )}
                                            </FGen>
                                            <FGen name="qtyWaste" label="Phế phẩm (Hỏng)">
                                                {(field: any) => (
                                                    <NumericInput className="text-rose-600" value={field.state.value} onChange={(e) => field.handleChange(Number(e.target.value))} />
                                                )}
                                            </FGen>
                                        </div>
                                        <div className="grid grid-cols-3 gap-4">
                                            <FGen name="parameters.speed" label="Tốc độ (Speed)">
                                                {(field: any) => (
                                                    <Input placeholder="VD: 5000/h" value={field.state.value || ''} onChange={(e) => field.handleChange(e.target.value)} />
                                                )}
                                            </FGen>
                                            <FGen name="parameters.pressure" label="Áp lực (Pressure)">
                                                {(field: any) => (
                                                    <Input placeholder="VD: 150 bar" value={field.state.value || ''} onChange={(e) => field.handleChange(e.target.value)} />
                                                )}
                                            </FGen>
                                            <FGen name="parameters.temperature" label="Nhiệt độ (Temp)">
                                                {(field: any) => (
                                                    <Input placeholder="VD: 120 C" value={field.state.value || ''} onChange={(e) => field.handleChange(e.target.value)} />
                                                )}
                                            </FGen>
                                        </div>
                                        {(watchQtyWaste as number) > 0 && (
                                            <div className="mt-4 p-4 bg-rose-50 border border-rose-100 rounded-lg space-y-4 animate-in fade-in slide-in-from-top-2">
                                                <h4 className="text-sm font-bold text-rose-800 flex items-center gap-2">
                                                    <AlertTriangle className="w-4 h-4" /> Báo cáo Hàng lỗi (NG)
                                                </h4>
                                                <FGen name="reason" label="Lý do lỗi (Bắt buộc nếu không có ảnh)">
                                                    {(field: any) => (
                                                        <Textarea placeholder="Mô tả nguyên nhân gây lỗi..." className="border-rose-200 focus-visible:ring-rose-500 bg-white" value={field.state.value || ''} onChange={(e) => field.handleChange(e.target.value)} />
                                                    )}
                                                </FGen>
                                                <FGen name="attachments" label="Hình ảnh lỗi (Tối đa 3 ảnh)">
                                                    {(field: any) => {
                                                        const attachments = Array.isArray(field.state.value) ? field.state.value : [];
                                                        const slots = attachments.length < 3 ? [...attachments, ''] : attachments.slice(0, 3);
                                                        
                                                        return (
                                                            <div className="grid grid-cols-3 gap-2">
                                                                {slots.map((url: string, index: number) => (
                                                                    <ImageDropzone
                                                                        key={index}
                                                                        value={url}
                                                                        onChange={(newUrl: string) => {
                                                                            const newArr = [...attachments];
                                                                            if (newUrl) {
                                                                                newArr[index] = newUrl;
                                                                            } else {
                                                                                newArr.splice(index, 1);
                                                                            }
                                                                            field.handleChange(newArr.filter(Boolean));
                                                                        }}
                                                                    />
                                                                ))}
                                                            </div>
                                                        );
                                                    }}
                                                </FGen>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* DOWNTIME SPECIFIC FIELDS */}
                                {(watchType === 'DOWNTIME' || watchType === 'MAINTENANCE') && (
                                    <div className="bg-rose-50/50 p-4 rounded-lg border border-rose-100 space-y-4 animate-in fade-in slide-in-from-top-2">
                                        <h3 className="text-sm font-bold text-rose-800 uppercase tracking-widest flex items-center gap-2">
                                            <AlertTriangle className="w-4 h-4" /> Chi tiết Dừng máy
                                        </h3>

                                        <div className="grid grid-cols-2 gap-4">
                                            <FGen name="issueType" label="Loại sự cố / Lý do dừng máy">
                                                {(field: any) => (
                                                    <ComboboxConfigMaster
                                                        categoryCode="downtime_reasons"
                                                        value={field.state.value || ''}
                                                        onChange={field.handleChange}
                                                        placeholder="Chọn lý do dừng máy..."
                                                        hideLabel
                                                    />
                                                )}
                                            </FGen>
                                            <FGen name="durationMinutes" label="Thời gian dừng (phút)">
                                                {(field: any) => (
                                                    <NumericInput className="border-rose-300 focus:ring-rose-500" value={field.state.value} onChange={(e) => field.handleChange(Number(e.target.value))} />
                                                )}
                                            </FGen>
                                        </div>

                                        <FGen name="reason" label="Nguyên nhân cụ thể">
                                            {(field: any) => (
                                                <Textarea placeholder="Mô tả chi tiết nguyên nhân..." value={field.state.value || ''} onChange={(e) => field.handleChange(e.target.value)} />
                                            )}
                                        </FGen>

                                        <FGen name="fixAction" label="Biện pháp khắc phục">
                                            {(field: any) => (
                                                <Textarea placeholder="Đã xử lý như thế nào..." value={field.state.value || ''} onChange={(e) => field.handleChange(e.target.value)} />
                                            )}
                                        </FGen>
                                    </div>
                                )}
                            </>
                        )}
                    </form.Subscribe>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Hủy</Button>
                        <form.Subscribe
                            selector={(state) => [state.canSubmit, state.isSubmitting]}
                            children={([canSubmit, isSubmitting]) => (
                                <Button type="submit" data-testid="machine-log-submit" disabled={!canSubmit} className="bg-indigo-600 hover:bg-indigo-700 min-w-[120px]">
                                    {isSubmitting ? 'Đang lưu...' : (isEditMode ? "Lưu thay đổi" : "Lưu Nhật ký")}
                                </Button>
                            )}
                        />
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
